import { useEffect, useRef, useState } from 'react'

import {
  EXPAND_HEIGHT_DURATION,
  prefersReducedMotion,
  REVEAL_DURATION,
} from './complianceAnimations'

/**
 * True while the question list is still moving, so the caller can stop taking
 * pointer input: a click landing mid-slide hits whatever moved under it.
 *
 * Keyed on a per-question collapsed/expanded signature rather than handlers,
 * because a question also collapses when the active question moves, which no
 * handler sees.
 */
export const useMotionGuard = (rowStates: string): boolean => {
  const [isSettling, setIsSettling] = useState(false)
  const previous = useRef<string>()
  const timer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(timer.current), [])

  useEffect(() => {
    const before = previous.current
    previous.current = rowStates
    // First render snaps rather than animating.
    if (before === undefined || before === rowStates) return
    if (prefersReducedMotion()) return
    // A row appearing or disappearing is a reveal, which runs longer.
    const seconds =
      before.length === rowStates.length
        ? EXPAND_HEIGHT_DURATION
        : REVEAL_DURATION
    setIsSettling(true)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setIsSettling(false), seconds * 1000)
  }, [rowStates])

  return isSettling
}
